import { Uatraits } from '@yandex-int/k-common';
import React from 'react';
import ReactDOM from 'react-dom';

import { isInternetExplorer } from '../../utils/client-browser';
import withUatraits from '../../utils/hocs/with-uatraits';

import KeyboardContext, { KeyboardContextType } from './keyboard-context';
import { KEYBOARD_TYPES } from './types';

type Props = React.PropsWithChildren<{
    uatraits: Uatraits;
    onTrackKeypress?: (...args: unknown[]) => void;
}>;

class KeyboardContextProvider extends React.Component<Props> {
    constructor(props: Props) {
        super(props);

        const { uatraits } = props;
        const isMobile = uatraits && Boolean(uatraits.isMobile);

        this.state = {
            [KEYBOARD_TYPES.PRIMARY]: {
                handleKeyboardArrow: this.handleKeyboardArrow,
                shouldShowKeyboard: false,
                shouldDisableNativeMobileKeyboard: isMobile,
                keyboardAllowed: !isInternetExplorer(uatraits),
                setRef: (ref: HTMLElement | null) => this.setRef(ref, KEYBOARD_TYPES.PRIMARY),
                handleTrackKeypress: this.handleTrackKeypress,
            },
            [KEYBOARD_TYPES.SECONDARY]: {
                shouldShowKeyboard: false,
                shouldDisableNativeMobileKeyboard: false,
                keyboardAllowed: true,
                setRef: (ref: HTMLElement | null) => this.setRef(ref, KEYBOARD_TYPES.SECONDARY),
            },
            [KEYBOARD_TYPES.PROBLEM_BAR]: {
                shouldShowKeyboard: false,
                shouldDisableNativeMobileKeyboard: false,
                keyboardAllowed: true,
                setRef: (ref: HTMLElement | null) => this.setRef(ref, KEYBOARD_TYPES.PROBLEM_BAR),
            },
            [KEYBOARD_TYPES.PROBLEM_BAR_SECONDARY]: {
                shouldShowKeyboard: false,
                shouldDisableNativeMobileKeyboard: false,
                keyboardAllowed: true,
                setRef: (ref: HTMLElement | null) => this.setRef(ref, KEYBOARD_TYPES.PROBLEM_BAR_SECONDARY),
            },
            [KEYBOARD_TYPES.PROBLEM_BAR_ROVER]: {
                shouldShowKeyboard: false,
                shouldDisableNativeMobileKeyboard: false,
                keyboardAllowed: true,
                setRef: (ref: HTMLElement | null) => this.setRef(ref, KEYBOARD_TYPES.PROBLEM_BAR_ROVER),
            },
            show: this.show,
            refs: {},
        };
    }

    state: KeyboardContextType;

    keyboardUser: Partial<Record<KEYBOARD_TYPES, symbol>> = {};

    setRef = (ref: HTMLElement | null, type: KEYBOARD_TYPES) => {
        this.setState((state: KeyboardContextType) => {
            const { refs } = state;

            if (refs[type] !== ref && ref) {
                return {
                    refs: {
                        ...refs,
                        [type]: ref,
                    },
                };
            }

            return undefined;
        });
    };

    /**
     * Показать клавиатуру.
     *
     * Возвращает контролы - объект с двумя методами - hide и render
     * - render позволяет отрендерить содержимое в клавиатуру
     * - hide скрывает клавиатуру
     *
     * При вызове сохраняем уникальное значение в this.keyboardUser
     * При последующем вызове вернутся новые контролы, а предыдущие перестанут работать
     *
     * На случай, например, если при переключении на другой инпут
     * onFocus у нового сработает раньше, чем onBlur у старого
     * Тогда после показа клавиатуры новым инпутом старый уже не сможет ее скрыть
     */
    show = (type: KEYBOARD_TYPES) => {
        const symbol = Symbol('unique keyboard user');
        this.keyboardUser[type] = symbol;

        this.toggleKeyboardVisibility(type, true);

        const controls = {
            // delay hide to prevent flickering when keyboard hides and then shows shortly after
            hide: () =>
                setTimeout(() => {
                    if (this.keyboardUser[type] !== symbol) {
                        return;
                    }
                    this.toggleKeyboardVisibility(type, false);
                }, 10),
            render: (content: HTMLElement) => {
                // по хорошему надо определить в файле с каждым типом клавиатуры функцию валидации, может ли ее
                // использовать этот компонент, но это требует серьезного рефакторинга
                if (this.keyboardUser[type] !== symbol) {
                    return null;
                }

                const { refs } = this.state;

                //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: Хочет ReactNode вместo JSX.Element
                return refs[type] ? ReactDOM.createPortal(content, refs[type] as Element) : null;
            },
        };

        return controls;
    };

    toggleKeyboardVisibility = (type: KEYBOARD_TYPES, visible: boolean) => {
        this.setState((state: KeyboardContextType) => {
            const newKeyboardConfig = { ...state[type] };
            newKeyboardConfig.shouldShowKeyboard = visible;

            return { ...state, [type]: newKeyboardConfig };
        });
    };

    /**
     * Move focus to next field.
     */
    handleKeyboardArrow = () => {
        const inputs = [...document.querySelectorAll('[data-switchable="true"]')];
        const focusedInputIndex = inputs.findIndex((input) => input === document.activeElement);

        let nextInputIndex;
        const offset = 1;

        if (focusedInputIndex + offset === inputs.length) {
            //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: ??
            document.activeElement?.blur();
            // const htmlElement = document.activeElement;
            // if (htmlElement && htmlElement instanceof HTMLElement) {
            //     htmlElement.focus();
            // }
            return;
        } else if (focusedInputIndex + offset === -1) {
            nextInputIndex = inputs.length - 1;
        } else {
            nextInputIndex = focusedInputIndex + offset;
        }

        //@ts-ignore - EDUCATION-37077: [react-update]: fixme typings: ??
        inputs[nextInputIndex].focus();

        // const input = inputs[nextInputIndex];
        // if (input && input instanceof HTMLElement) {
        //     input.focus();
        // }
    };

    handleTrackKeypress = (...args: unknown[]) => {
        const { onTrackKeypress } = this.props;

        if (onTrackKeypress) {
            onTrackKeypress(...args);
        }
    };

    render() {
        return <KeyboardContext.Provider value={this.state}>{this.props.children}</KeyboardContext.Provider>;
    }
}

export default withUatraits(KeyboardContextProvider);
